import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Alert,
  AlertIcon,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  Avatar
} from '@chakra-ui/react';
import { ChevronDownIcon, AddIcon, CloseIcon } from '@chakra-ui/icons';
import { fightService } from '../../services/fightService';
import { fighterService } from '../../services/fighterService';
import { useDataStore } from '../../contexts/DataStoreContext';

export const AddFightForm = ({ eventId, onFightAdded }) => {
  const dataStore = useDataStore();
  const [fighter1, setFighter1] = useState(null);
  const [fighter2, setFighter2] = useState(null);
  const [fighter1Search, setFighter1Search] = useState('');
  const [fighter2Search, setFighter2Search] = useState('');
  const [fighter1Record, setFighter1Record] = useState('');
  const [fighter2Record, setFighter2Record] = useState('');
  const [availableFighters, setAvailableFighters] = useState(/** @type {Array} */([]));
  const [weightClass, setWeightClass] = useState('');
  const [rounds, setRounds] = useState('3');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Focus management
  const fighter1InputRef = useRef(/** @type {HTMLInputElement|null} */(null));
  const fighter2InputRef = useRef(/** @type {HTMLInputElement|null} */(null));
  const fighter1RecordRef = useRef(/** @type {HTMLInputElement|null} */(null));
  const fighter2RecordRef = useRef(/** @type {HTMLInputElement|null} */(null));
  const weightClassRef = useRef(/** @type {HTMLSelectElement|null} */(null));
  const roundsRef = useRef(/** @type {HTMLSelectElement|null} */(null));
  const lastFocusedInput = useRef(/** @type {string|null} */(null));

  const toast = useToast();

  const weightClasses = [
    'Strawweight', 'Flyweight', 'Bantamweight', 'Featherweight',
    'Lightweight', 'Welterweight', 'Middleweight', 'Light Heavyweight',
    'Heavyweight', 'Super Heavyweight'
  ];

  useEffect(() => {
    loadFighters();
  }, []);

  // Update available fighters when DataStore fighters change
  useEffect(() => {
    const fighters = dataStore.getAllFighters();
    setAvailableFighters(fighters);
  }, [dataStore.fighters]);

  // Restore focus after re-renders
  useEffect(() => {
    if (lastFocusedInput.current) {
      let inputRef;
      switch (lastFocusedInput.current) {
        case 'fighter1':
          inputRef = fighter1InputRef;
          break;
        case 'fighter2':
          inputRef = fighter2InputRef;
          break;
        case 'fighter1Record':
          inputRef = fighter1RecordRef;
          break;
        case 'fighter2Record':
          inputRef = fighter2RecordRef;
          break;
        case 'weightClass':
          inputRef = weightClassRef;
          break;
        case 'rounds':
          inputRef = roundsRef;
          break;
        default:
          inputRef = null;
      }

      if (inputRef && inputRef.current && inputRef.current.focus && document.activeElement !== inputRef.current) {
        // Only restore focus if no other element currently has focus or if focus was lost
        const activeElement = document.activeElement;
        if (!activeElement || activeElement === document.body) {
          inputRef.current.focus();
        }
      }
    }
  });

  const loadFighters = () => {
    try {
      // Use DataStore instead of service for read operations
      const fighters = dataStore.getAllFighters();
      setAvailableFighters(fighters);
    } catch (err) {
      console.error('Failed to load fighters:', err);
    }
  };

  const handleFighterSelect = (fighter, fighterNumber) => {
    if (fighterNumber === 1) {
      setFighter1(fighter);
      setFighter1Search(fighter ? fighter.name : '');
      setFighter1Record(fighter ? fighter.record || '' : '');
    } else if (fighterNumber === 2) {
      setFighter2(fighter);
      setFighter2Search(fighter ? fighter.name : '');
      setFighter2Record(fighter ? fighter.record || '' : '');
    }

    // Prefill weight class if not already selected and fighter has a weight class
    if (fighter && fighter.weightClass && !weightClass) {
      setWeightClass(fighter.weightClass);
    }
  };

  const createNewFighter = async (name, record, fighterNumber) => {
    if (!name.trim()) return;

    try {
      const fighterData = {
        name: name.trim(),
        weightClass
      };
      
      // Only add record if it has a value
      if (record && record.trim()) {
        fighterData.record = record.trim();
      }

      const newFighter = await fighterService.getOrCreateFighter(fighterData, dataStore);

      handleFighterSelect(newFighter, fighterNumber);
      await loadFighters(); // Refresh the list
    } catch (err) {
      console.error('Failed to create fighter:', err);
    }
  };

  const getFilteredFighters = (searchTerm) => {
    if (!searchTerm) return availableFighters;
    return availableFighters.filter(fighter =>
      fighter.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Ensure we have fighters selected or create them
      let finalFighter1 = fighter1;
      let finalFighter2 = fighter2;

      if (!finalFighter1 && fighter1Search.trim()) {
        await createNewFighter(fighter1Search, fighter1Record, 1);
        finalFighter1 = fighter1; // Should be set by createNewFighter
      }

      if (!finalFighter2 && fighter2Search.trim()) {
        await createNewFighter(fighter2Search, fighter2Record, 2);
        finalFighter2 = fighter2; // Should be set by createNewFighter
      }

      if (!finalFighter1 || !finalFighter2) {
        setError('Please select or create both fighters');
        return;
      }

      // Check if the same fighter is selected for both positions
      if (finalFighter1.id === finalFighter2.id) {
        setError('Cannot select the same fighter for both positions');
        return;
      }

      await fightService.addFightToEvent(eventId, {
        fighter1: finalFighter1,
        fighter2: finalFighter2,
        weightClass,
        rounds: parseInt(rounds),
        status: 'upcoming'
      }, dataStore);

      toast({
        title: 'Fight added successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset form
      setFighter1(null);
      setFighter2(null);
      setFighter1Search('');
      setFighter2Search('');
      setFighter1Record('');
      setFighter2Record('');
      setWeightClass('');
      setRounds('3');

      onFightAdded();
    } catch (err) {
      setError(err.message || 'Failed to add fight');
    } finally {
      setLoading(false);
    }
  };

  const FighterSelector = ({
    label,
    value,
    searchValue,
    onSearchChange,
    record,
    onRecordChange,
    onFighterSelect,
    fighterNumber,
    inputRef,
    recordRef
  }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const filteredFighters = getFilteredFighters(searchValue);
    const hasExactMatch = filteredFighters.some(f => f?.name?.toLowerCase() === searchValue.toLowerCase());
    
    // Get the other fighter to prevent duplicate selection
    const otherFighter = fighterNumber === 1 ? fighter2 : fighter1;

    return (
      <VStack spacing={2} align="stretch">
        <FormControl isRequired>
          <FormLabel>{label}</FormLabel>
          <Box position="relative">
            {value && value.id ? (
              // Show selected fighter with option to change
              <Box
                p={3}
                bg="green.900"
                borderRadius="md"
                border="2px solid"
                borderColor="green.500"
              >
                <HStack justify="space-between" align="center">
                  <HStack spacing={3} flex={1}>
                    <Avatar
                      size="md"
                      name={value.name}
                      src={value.photoUrl}
                      color="gray.700"
                      bg="white"
                    />
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontWeight="bold" color="green.100">
                        {value.name}
                      </Text>
                      {value.record && (
                        <Text fontSize="sm" color="green.200">
                          Record: {value.record}
                        </Text>
                      )}
                      {value.weightClass && (
                        <Text fontSize="sm" color="green.300">
                          {value.weightClass}
                        </Text>
                      )}
                    </VStack>
                  </HStack>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    icon={<CloseIcon />}
                    onClick={() => {
                      // Clear selection completely
                      onFighterSelect(null, fighterNumber);
                      onSearchChange('');
                      onRecordChange('');
                    }}
                    aria-label="Remove fighter"
                    title="Remove fighter"
                  />
                </HStack>
              </Box>
            ) : (
              // Show search input for selecting/creating fighter
              <InputGroup>
                <Input
                  ref={inputRef}
                  value={searchValue}
                  onChange={(e) => {
                    onSearchChange(e.target.value);
                    if (e.target.value && !showDropdown) {
                      setShowDropdown(true);
                    }
                  }}
                  onFocus={() => {
                    setShowDropdown(true);
                    lastFocusedInput.current = fighterNumber === 1 ? 'fighter1' : 'fighter2';
                  }}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                  placeholder="Search or type new fighter name"
                  bg="dark.700"
                  borderColor="dark.600"
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                />
                <InputRightElement>
                  <ChevronDownIcon
                    cursor="pointer"
                    onClick={() => {
                      if (!showDropdown && !searchValue) {
                        // Show all fighters when clicking chevron with no search
                        setShowDropdown(true);
                      }
                    }}
                  />
                </InputRightElement>
              </InputGroup>
            )}

            {showDropdown && !value?.id && (searchValue || filteredFighters.length > 0) && (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                zIndex={1000}
                bg="dark.700"
                border="1px solid"
                borderColor="dark.600"
                borderRadius="md"
                boxShadow="lg"
                maxH="200px"
                overflowY="auto"
                mt={1}
              >
                {filteredFighters.map(fighter => {
                  const isAlreadySelected = otherFighter && otherFighter.id === fighter.id;
                  return (
                    <Box
                      key={fighter.id}
                      p={3}
                      cursor={isAlreadySelected ? 'not-allowed' : 'pointer'}
                      _hover={{ bg: isAlreadySelected ? 'red.900' : 'dark.600' }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        if (isAlreadySelected) {
                          toast({
                            title: 'Fighter already selected',
                            description: `${fighter.name} is already selected for the other position`,
                            status: 'warning',
                            duration: 3000,
                            isClosable: true,
                          });
                          return;
                        }
                        onFighterSelect(fighter, fighterNumber);
                        setShowDropdown(false);
                      }}
                      bg={isAlreadySelected ? 'red.900' : 'transparent'}
                      opacity={isAlreadySelected ? 0.6 : 1}
                    >
                    <HStack spacing={3} align="center">
                      <Avatar
                        size="sm"
                        name={fighter.name}
                        src={fighter.photoUrl}
                        color="gray.700"
                        bg="white"
                      />
                      <VStack align="start" spacing={0} flex={1}>
                        <HStack spacing={2} align="center">
                          <Text fontWeight="medium" color={isAlreadySelected ? "red.300" : "white"}>
                            {fighter.name}
                          </Text>
                          {isAlreadySelected && (
                            <Text fontSize="xs" color="red.400" fontWeight="bold">
                              (Already Selected)
                            </Text>
                          )}
                        </HStack>
                        {fighter.record && (
                          <Text fontSize="sm" color={isAlreadySelected ? "red.400" : "gray.400"}>
                            Record: {fighter.record}
                          </Text>
                        )}
                        {fighter.weightClass && (
                          <Text fontSize="sm" color={isAlreadySelected ? "red.400" : "blue.400"}>
                            {fighter.weightClass}
                          </Text>
                        )}
                      </VStack>
                    </HStack>
                  </Box>
                  );
                })}
                {searchValue && !hasExactMatch && (
                  <Box
                    p={3}
                    cursor="pointer"
                    _hover={{ bg: 'dark.600' }}
                    color="blue.400"
                    fontWeight="medium"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      createNewFighter(searchValue, record, fighterNumber);
                      setShowDropdown(false);
                    }}
                  >
                    <HStack>
                      <AddIcon boxSize={3} />
                      <Text>Create "{searchValue}"</Text>
                    </HStack>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </FormControl>

        <FormControl>
          <FormLabel>Record</FormLabel>
          <Input
            ref={recordRef}
            value={record}
            onChange={(e) => onRecordChange(e.target.value)}
            onFocus={() => {
              lastFocusedInput.current = `fighter${fighterNumber}Record`;
            }}
            placeholder="e.g., 10-2-0"
            size="sm"
            isReadOnly={value && value.id} // Read-only if existing fighter is selected
            bg={value && value.id ? 'dark.600' : 'dark.700'}
            color="white"
            _placeholder={{ color: 'gray.400' }}
            _readOnly={{
              cursor: 'not-allowed',
              opacity: 0.8,
              bg: 'dark.600',
              color: 'gray.300'
            }}
          />
          {value && value.id && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              Record is from existing fighter profile
            </Text>
          )}
        </FormControl>
      </VStack>
    );
  };

  return (
    <Box maxW="lg" mx="auto" p={6} borderWidth={1} borderRadius="lg" shadow="lg">
      <Text fontSize="xl" fontWeight="bold" mb={4}>
        Add Fight to Event
      </Text>

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <VStack spacing={4}>
          <HStack width="full" spacing={4} align="start">
            <FighterSelector
              key="fighter-1"
              label="Fighter 1"
              value={fighter1}
              searchValue={fighter1Search}
              onSearchChange={setFighter1Search}
              record={fighter1Record}
              onRecordChange={setFighter1Record}
              onFighterSelect={handleFighterSelect}
              fighterNumber={1}
              inputRef={fighter1InputRef}
              recordRef={fighter1RecordRef}
            />

            <FighterSelector
              key="fighter-2"
              label="Fighter 2"
              value={fighter2}
              searchValue={fighter2Search}
              onSearchChange={setFighter2Search}
              record={fighter2Record}
              onRecordChange={setFighter2Record}
              onFighterSelect={handleFighterSelect}
              fighterNumber={2}
              inputRef={fighter2InputRef}
              recordRef={fighter2RecordRef}
            />
          </HStack>

          <HStack width="full" spacing={4}>
            <FormControl isRequired>
              <FormLabel>Weight Class</FormLabel>
              <Select
                ref={weightClassRef}
                value={weightClass}
                onChange={(e) => setWeightClass(e.target.value)}
                onFocus={() => {
                  lastFocusedInput.current = 'weightClass';
                }}
                placeholder="Select weight class"
              >
                {weightClasses.map(wc => (
                  <option key={wc} value={wc}>{wc}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Rounds</FormLabel>
              <Select
                ref={roundsRef}
                value={rounds}
                onChange={(e) => setRounds(e.target.value)}
                onFocus={() => {
                  lastFocusedInput.current = 'rounds';
                }}
              >
                <option value="1">1 Round</option>
                <option value="3">3 Rounds</option>
                <option value="5">5 Rounds</option>
              </Select>
            </FormControl>
          </HStack>

          <Button
            type="submit"
            colorScheme="blue"
            width="full"
            isLoading={loading}
            loadingText="Adding fight..."
          >
            Add Fight
          </Button>
        </VStack>
      </form>
    </Box>
  );
};
